'use es6';

import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { getQuickReply } from 'conversations-message-history/common-message-format/operators/cmfQuickReplyGetters';
import { getOptionLabel, getOptionValue } from 'conversations-message-history/quick-reply/operators/quickReplyGetters';
import { List, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import { Component } from 'react';
import VizExCheckbox from 'visitor-ui-component-library/input/VizExCheckbox';
import VizExButton from 'visitor-ui-component-library/button/VizExButton';
import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import SVGSuccess from 'visitor-ui-component-library-icons/icons/SVGSuccess';
import styled from 'styled-components';
import ThemeContext from '../theming/contexts/ThemeContext';
import { WHITE } from 'visitor-ui-component-library/theme/ColorConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const ButtonWrapperInsideChat = styled.div.withConfig({
  displayName: "QuickReplyGroup__ButtonWrapperInsideChat",
  componentId: "sc-1ehzyb1-0"
})(["margin-bottom:12px;gap:8px;padding-top:8px;padding-bottom:8px;align-items:center;display:flex;flex-wrap:wrap;"]);
const ButtonWrapperOutsideChat = styled.div.withConfig({
  displayName: "QuickReplyGroup__ButtonWrapperOutsideChat",
  componentId: "sc-1ehzyb1-1"
})(["margin-bottom:0px;gap:16px;padding-top:8px;padding-bottom:0px;align-items:normal;display:inline-flex;flex-direction:column;flex-wrap:wrap;width:240px;"]);
const LeftAlignButton = styled(VizExButton).withConfig({
  displayName: "QuickReplyGroup__LeftAlignButton",
  componentId: "sc-1ehzyb1-2"
})(["text-align:left;flex-shrink:1;"]);
const QuickReplyOutsideChatButton = styled(VizExButton).withConfig({
  displayName: "QuickReplyGroup__QuickReplyOutsideChatButton",
  componentId: "sc-1ehzyb1-3"
})(["background:", ";"], WHITE);
class QuickReplyGroup extends Component {
  constructor(props) {
    super(props);
    this.handleSendMulti = this.handleSendMulti.bind(this);
    this.handleQuickReplySend = this.handleQuickReplySend.bind(this);
    this.handleSendIndividual = this.handleSendIndividual.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.state = {
      selected: ImmutableSet()
    };
  }
  hasSelected() {
    const {
      selected
    } = this.state;
    return selected.size > 0;
  }
  handleSelect(option) {
    this.setState(({
      selected
    }) => {
      const updatedSelected = selected.includes(option) ? selected.delete(option) : selected.add(option);
      return {
        selected: updatedSelected
      };
    });
  }
  handleSendMulti() {
    const {
      selected
    } = this.state;
    if (this.hasSelected()) {
      const text = selected.map(getOptionLabel).join(', ');
      this.handleQuickReplySend(text, selected);
    }
  }
  handleSendIndividual(option) {
    const options = List([option]);
    const text = getOptionLabel(option);
    this.handleQuickReplySend(text, options);
  }
  handleQuickReplySend(text, options) {
    const {
      message,
      publishMessage
    } = this.props;
    const quickReply = getQuickReply(message);
    const updatedQuickReply = quickReply.set('quickReplies', options.toJS());
    publishMessage({
      text,
      quickReply: updatedQuickReply
    });
  }
  renderOptions() {
    const {
      options,
      multiSelect,
      disabled,
      isOutsideChat,
      onSend,
      theme
    } = this.props;
    const {
      selected
    } = this.state;
    const onClick = typeof onSend === 'function' ? onSend : multiSelect ? this.handleSelect : this.handleSendIndividual;
    return options.map(option => {
      const boundOnClick = () => onClick(option);
      const checked = selected.includes(option);
      const value = getOptionValue(option);
      const label = getOptionLabel(option);
      if (!value || !label) {
        return null;
      }
      const checkbox = multiSelect && /*#__PURE__*/_jsx(VizExCheckbox, {
        className: "m-right-2",
        checked: checked,
        onChange: boundOnClick,
        children: label
      });
      return isOutsideChat ? /*#__PURE__*/_jsx(QuickReplyOutsideChatButton, {
        onClick: boundOnClick,
        disabled: disabled,
        theme: theme,
        children: label
      }, `quickreply.${value}`) : /*#__PURE__*/_jsx(LeftAlignButton, {
        onClick: boundOnClick,
        disabled: disabled,
        size: "sm",
        children: checkbox || label
      }, `quickreply.${value}`);
    });
  }
  renderSendButton() {
    const {
      disabled,
      multiSelect
    } = this.props;
    if (!multiSelect) {
      return null;
    }
    const disableQuickReply = !this.hasSelected() || disabled;
    return /*#__PURE__*/_jsx(VizExIconButton, {
      disabled: disableQuickReply,
      onClick: this.handleSendMulti,
      size: "sm",
      "data-test-id": "quick-reply-send-button",
      children: /*#__PURE__*/_jsx(VizExIcon, {
        icon: /*#__PURE__*/_jsx(SVGSuccess, {}),
        size: "sm"
      })
    }, "send-button");
  }
  render() {
    const {
      isOutsideChat
    } = this.props;
    const ButtonWrapper = isOutsideChat ? ButtonWrapperOutsideChat : ButtonWrapperInsideChat;
    return /*#__PURE__*/_jsx(ButtonWrapper, {
      "data-test-id": "quick-reply-group",
      children: [...this.renderOptions(), this.renderSendButton()]
    });
  }
}
QuickReplyGroup.contextType = ThemeContext;
QuickReplyGroup.defaultProps = {
  multiSelect: false,
  hasSentReply: false,
  isOutsideChat: false
};
QuickReplyGroup.displayName = 'QuickReplyGroup';
QuickReplyGroup.propTypes = {
  disabled: PropTypes.bool.isRequired,
  isOutsideChat: PropTypes.bool,
  message: PropTypes.oneOfType([RecordPropType('CommonMessage'), RecordPropType('InitialMessage')]).isRequired,
  multiSelect: PropTypes.bool.isRequired,
  onSend: PropTypes.func,
  options: PropTypes.instanceOf(ImmutableSet).isRequired,
  publishMessage: PropTypes.func,
  theme: PropTypes.object
};
export default QuickReplyGroup;